import React, { useEffect, useRef, useState } from "react";
import { FiDownload } from "react-icons/fi";
import { toast } from "react-toastify";
import { contexto, dossie } from "../mockData";
import validacaoFiscalService from "../../../services/validacaoFiscalService";

export default function DossieTab({ competenciaId }) {
  const usandoReal = !!competenciaId;
  const [real, setReal] = useState(null);
  const [parecer, setParecer] = useState("");
  const [gerando, setGerando] = useState(false);
  const [aprovando, setAprovando] = useState(false);
  const pollRef = useRef(null);

  const carregar = () => {
    if (!competenciaId) return;
    validacaoFiscalService
      .obterDossie(competenciaId)
      .then((resp) => setReal(resp?.data?.data || null))
      .catch((err) => console.error("[DossieTab] Erro:", err));
  };

  useEffect(() => {
    if (competenciaId) carregar();
    else setReal(null);
    return () => clearTimeout(pollRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [competenciaId]);

  // Enquanto o PDF ainda está sendo renderizado pelo WORKER, faz polling leve.
  useEffect(() => {
    clearTimeout(pollRef.current);
    if (real && !real.pdfPath && real.status === "rascunho") {
      pollRef.current = setTimeout(carregar, 4000);
    }
    return () => clearTimeout(pollRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [real]);

  const handleGerar = async () => {
    if (!parecer) {
      toast.error("Parecer do responsável técnico é obrigatório.");
      return;
    }
    setGerando(true);
    try {
      await validacaoFiscalService.gerarDossie(competenciaId, parecer);
      toast.success("Dossiê solicitado — o PDF está sendo gerado.");
      carregar();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Erro ao gerar dossiê.");
    } finally {
      setGerando(false);
    }
  };

  const handleAprovar = async () => {
    if (!real?.pdfPath) {
      toast.info("O PDF ainda está sendo gerado — aguarde alguns segundos.");
      return;
    }
    setAprovando(true);
    try {
      await validacaoFiscalService.aprovarDossie(real.id);
      toast.success("Dossiê aprovado e emitido.");
      carregar();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Erro ao aprovar dossiê.");
    } finally {
      setAprovando(false);
    }
  };

  if (usandoReal && !real) {
    return (
      <div className="dossie-toolbar">
        <div className="dossie-toolbar-text">
          Nenhum dossiê gerado para esta competência ainda. Preencha o parecer do responsável técnico e
          emita a pré-visualização.
        </div>
        <label className="decision-field-label" style={{ width: "100%", maxWidth: 880, margin: "12px auto" }}>
          Parecer do responsável técnico
          <textarea rows={4} value={parecer} onChange={(e) => setParecer(e.target.value)} />
        </label>
        <div className="dossie-toolbar-actions">
          <button type="button" className="icon-btn primary" onClick={handleGerar} disabled={gerando}>
            {gerando ? "Gerando…" : "Gerar dossiê"}
          </button>
        </div>
      </div>
    );
  }

  const dados = usandoReal ? real.snapshot : { ...dossie, contexto, parecer: dossie.parecer || "" };
  const statusLabel = usandoReal
    ? real.status === "aprovado"
      ? "aprovado e emitido"
      : real.pdfPath
      ? "rascunho, aguardando aprovação"
      : "rascunho, gerando PDF…"
    : "rascunho, aguardando aprovação";

  return (
    <>
      <div className="dossie-toolbar">
        <div className="dossie-toolbar-text">
          Pré-visualização do dossiê mensal · <strong>{statusLabel}</strong>
        </div>
        <div className="dossie-toolbar-actions">
          {usandoReal ? (
            <a
              className="icon-btn"
              href={real.pdfPath ? validacaoFiscalService.baixarDossieUrl(real.id) : undefined}
              target="_blank"
              rel="noreferrer"
              style={!real.pdfPath ? { opacity: 0.4, pointerEvents: "none" } : undefined}
            >
              <FiDownload /> Baixar PDF
            </a>
          ) : (
            <button type="button" className="icon-btn" disabled title="Disponível com uma competência real aberta">
              <FiDownload /> Baixar PDF
            </button>
          )}
          <button
            type="button"
            className="icon-btn primary"
            onClick={usandoReal ? handleAprovar : undefined}
            disabled={!usandoReal || aprovando || real?.status === "aprovado"}
            title={usandoReal ? undefined : "Disponível com uma competência real aberta"}
          >
            {aprovando ? "Aprovando…" : real?.status === "aprovado" ? "Aprovado" : "Aprovar e emitir"}
          </button>
        </div>
      </div>

      <article className="dossie-doc">
        <header className="dossie-header">
          <div className="dossie-brand">FH SOUZA.</div>
          <div className="dossie-brand-sub">ADVOGADOS</div>
          <h2>Dossiê de Validação Fiscal {(dados.contexto?.tributo || "CBS").toString().toUpperCase()}</h2>
          <div className="dossie-meta">
            {dados.contexto?.empresa || contexto.empresa} · CNPJ raiz {dados.contexto?.cnpjRaiz || contexto.cnpjRaiz} ·
            Competência {dados.contexto?.competencia || contexto.competencia} · Emitido em{" "}
            {dados.contexto?.emitidoEm || "[data de emissão]"}
          </div>
        </header>

        <section>
          <h3 className="dossie-section-title">1. Resumo do saldo</h3>
          <table>
            <thead>
              <tr>
                <th className="th">Componente</th>
                <th className="th" style={{ textAlign: "right" }}>
                  Valor (R$)
                </th>
                <th className="th">Base</th>
              </tr>
            </thead>
            <tbody>
              {dados.resumoSaldo.map((row) => (
                <tr key={row.componente}>
                  <td className="td">{row.destaque ? <strong>{row.componente}</strong> : row.componente}</td>
                  <td className="td" style={{ textAlign: "right", fontFamily: "'JetBrains Mono', monospace" }}>
                    {row.destaque ? <strong>{row.valor}</strong> : row.valor}
                  </td>
                  <td className="td">{row.base}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>

        <section>
          <h3 className="dossie-section-title">2. Conciliação AA × ERP × DF-e</h3>
          <div className="dossie-grid">
            {dados.conciliacao.map((item) => (
              <div className="dossie-grid-item" key={item.label}>
                <div className="dossie-grid-label">{item.label}</div>
                <div className="dossie-grid-value">{item.valor}</div>
              </div>
            ))}
          </div>
        </section>

        <section>
          <h3 className="dossie-section-title">3. Divergências e tratamento</h3>
          <table>
            <thead>
              <tr>
                <th className="th">Tipo</th>
                <th className="th">Qtde</th>
                <th className="th">Tratamento</th>
                <th className="th">Base legal</th>
              </tr>
            </thead>
            <tbody>
              {dados.divergenciasTratamento.map((row, i) => (
                <tr key={row.tipo || i}>
                  <td className="td">{row.tipo}</td>
                  <td className="td">{row.qtde}</td>
                  <td className="td">{row.tratamento}</td>
                  <td className="td">{row.baseLegal}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>

        <section>
          <h3 className="dossie-section-title">4. Decisões do período</h3>
          <ul className="dossie-list">
            {dados.decisoesPeriodo.map((item, i) => (
              <li key={i}>{item}</li>
            ))}
          </ul>
        </section>

        <section>
          <h3 className="dossie-section-title">5. Parecer do responsável técnico</h3>
          <div className="dossie-parecer">
            {dados.parecer ||
              "[Parecer a ser redigido pelo responsável técnico antes da emissão: conclusão sobre a validação do saldo, ressalvas e pendências.]"}
          </div>
        </section>

        <section className="dossie-signatures">
          <div className="dossie-signature">
            Responsável técnico · FH Souza
            <br />
            [nome e registro profissional]
          </div>
          <div className="dossie-signature">
            Aprovação do cliente
            <br />
            [nome e cargo]
          </div>
        </section>

        <footer className="dossie-footer">
          Anexos: extrato da AA (CSV), relatório de conciliação, evidências por divergência e trilha de
          auditoria. Este dossiê não substitui a confirmação ou o ajuste da AA no portal da Receita
          Federal, que deve ser feita dentro do prazo.
        </footer>
      </article>
    </>
  );
}
